/**
 * Картка рахунку (ч.1036 — переписано за каноном): проводки з наростаючим
 * сальдо, відбір по субконто (з ОСВ по рахунку) і кореспондуючому рахунку,
 * підсумки по місяцях, перехід у документ по кліку, друк і Excel.
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accounts, reports, type AccountCardRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';
import LedgerFilters, { NoRegulatedAccess } from './ledger/LedgerFilters';
import { useLedgerParams } from './ledger/useLedgerParams';
import { numCell, periodLabel, qtyCell } from './ledger/ledgerColumns';
import { ledgerLinks, money, monthLabel, monthSubtotals, signedBalance } from './ledger/ledgerModel';

type Row = Partial<AccountCardRow> & {
  _kind: 'open' | 'entry' | 'month' | 'close';
  _key: string;
  _label?: string;
  amount_dt: string; amount_cr: string; running_balance: string;
};

/** Залишок словами: «1 234,56 Дт» / «1 234,56 Кт» / «0,00». */
export function balanceText(value: number): string {
  const b = signedBalance(String(Math.round(value * 100) / 100));   // без «хвостів» дробів
  return b.side === 'cr' ? `${b.text} Кт` : b.side === 'dt' ? `${b.text} Дт` : '0,00';
}

const docLabel = (t: string | null) => (t ? (DOC_TABLE_NAMES_UK[t] ?? t.replace(/^doc\./, '')) : '');

export default function AccountCardPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const account = st.get('account');
  const sc = st.get('sc');
  const scName = st.get('scName');
  const corr = st.get('corr');
  const side = st.get('side') === 'DT' || st.get('side') === 'CR' ? (st.get('side') as 'DT' | 'CR') : undefined;
  const months = st.get('months') === '1';
  // Без рахунку, але з субконто — КАРТКА СУБКОНТО (усі рахунки).
  const subcontoMode = !account && !!sc;

  const accountsQ = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accounts.list({ only_postable: false, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const q = useQuery({
    queryKey: ['ledger-card', st.org, account, st.from, st.to, sc, corr, side],
    queryFn: () => reports.accountCard({
      organization_id: st.org, account_code: account || undefined, date_from: st.from, date_to: st.to,
      subconto_id: sc || undefined, corr_account_code: corr || undefined, side }),
    enabled: !!st.org && (!!account || subcontoMode),
  });
  const data = q.data;
  const period = { org: st.org, from: st.from, to: st.to };
  // Кількість показуємо, якщо вона є хоч в одній проводці (кількісний рахунок).
  const hasQty = !!data?.rows.some((r) => r.quantity != null && Number(r.quantity) !== 0);

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    const t = data.totals;
    const open = Number(t.opening_dt) - Number(t.opening_cr);
    const out: Row[] = [{ _kind: 'open', _key: '_open', _label: 'Сальдо на початок',
      amount_dt: '0', amount_cr: '0', running_balance: String(open) }];
    const subtotals = months ? monthSubtotals(data.rows) : [];
    let si = 0;
    data.rows.forEach((r, i) => {
      out.push({ ...r, _kind: 'entry', _key: `${r.entry_id}|${r.side}|${i}` });
      const next = data.rows[i + 1];
      const m = r.entry_date.slice(0, 7);
      if (months && (!next || next.entry_date.slice(0, 7) !== m)) {
        const s = subtotals[si++];
        if (s) out.push({ _kind: 'month', _key: `_m_${s.month}`, _label: `Разом за ${monthLabel(s.month)}`,
          amount_dt: String(s.turnover_dt), amount_cr: String(s.turnover_cr), running_balance: String(s.closing) });
      }
    });
    out.push({ _kind: 'close', _key: '_close', _label: 'Обороти і сальдо на кінець',
      amount_dt: t.turnover_dt, amount_cr: t.turnover_cr,
      running_balance: String(Number(t.closing_dt) - Number(t.closing_cr)) });
    return out;
  }, [data, months]);

  const openDoc = (r: Row) => {
    if (r._kind !== 'entry' || !r.document_type || !r.document_id) return;
    const to = DOC_ROUTES[r.document_type]?.(r.document_id);
    if (to) navigate(to);
  };

  // SUP-321707CF (ч.1239): ширини в сумі 968 — на екрані 1280 «Сальдо» видно без прокрутки
  // (було 1131: «Кредит» і «Сальдо» ховались за правий край).
  const cols = useMemo<ColumnDef<Row>[]>(() => [
    { key: 'entry_date', header: 'Дата', width: 80,
      cell: (r) => r._kind === 'entry' ? new Date(r.entry_date!).toLocaleDateString('uk-UA') : '' },
    ...(subcontoMode ? [{ key: 'account', header: 'Рахунок', width: 80,
      cell: (r: Row) => r._kind === 'entry' ? <span>{r.account_code}</span> : '' } as ColumnDef<Row>] : []),
    { key: 'document', header: 'Документ', width: 170,
      cell: (r) => {
        if (r._kind !== 'entry') return <strong>{r._label}</strong>;
        const text = `${docLabel(r.document_type ?? null)}${r.document_number ? ` № ${r.document_number}` : ''}`;
        const has = !!(r.document_type && r.document_id && DOC_ROUTES[r.document_type]);
        return has
          ? <a className="ledger__link" title="Відкрити документ" onClick={(e) => { e.stopPropagation(); openDoc(r); }}>{text}</a>
          : <span title="У цього типу документа немає сторінки">{text}</span>;
      } },
    { key: 'analytics', header: 'Аналітика', width: 130, cell: (r) => <span title={r.analytics}>{r.analytics ?? ''}</span> },
    { key: 'corr', header: 'Кор. рах.', width: 62,
      cell: (r) => r._kind === 'entry' ? <span title={r.corr_account_name ?? undefined}>{r.corr_account_code}</span> : '' },
    { key: 'corr_analytics', header: 'Аналітика кор.', width: 110, cell: (r) => <span title={r.corr_analytics}>{r.corr_analytics ?? ''}</span> },
    { key: 'content', header: 'Зміст', width: 120, cell: (r) => <span title={r.content ?? undefined}>{r.content ?? ''}</span> },
    { key: 'amount_dt', header: 'Дебет', width: 96, align: 'right', cell: (r) => numCell(r.amount_dt) },
    { key: 'amount_cr', header: 'Кредит', width: 96, align: 'right', cell: (r) => numCell(r.amount_cr) },
    ...(hasQty ? [{ key: 'quantity', header: 'Кіл.', width: 80, align: 'right',
      cell: (r: Row) => r._kind === 'entry' ? qtyCell(r.quantity) : '' } as ColumnDef<Row>] : []),
    { key: 'running_balance', header: 'Сальдо', width: 104, align: 'right',
      cell: (r) => {
        const b = signedBalance(r.running_balance);
        return <span className={`ledger__num${b.side === 'cr' ? ' ledger__num--cr' : ''}`}
          title={b.side === 'cr' ? 'кредитове сальдо' : b.side === 'dt' ? 'дебетове сальдо' : undefined}>
          {b.side === 'cr' ? `−${b.text}` : b.text}
        </span>;
      } },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [hasQty, subcontoMode]);

  if (!st.permsLoading && !st.hasPermission) {
    return <NoRegulatedAccess title="Картка рахунку" total={st.totalAccessibleOrgs} />;
  }
  const accList = (accountsQ.data ?? []).slice().sort((a, b) => a.code.localeCompare(b.code));
  const title = subcontoMode
    ? `Картка субконто: ${scName || 'усі рахунки'}`
    : data ? `Картка рахунку ${data.account_code} ${data.account_name}` : `Картка рахунку ${account || ''}`;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{title}{' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span></h2>
        {account && <Link to={ledgerLinks.trialBalanceAccount({ ...period, account })} className="btn-link">← ОСВ по рахунку {account}</Link>}
      </div>

      <LedgerFilters state={st}
        printHref={(account || subcontoMode) ? ledgerLinks.print('card', { ...period, account: account || undefined, sc: sc || undefined, scName: scName || undefined, corr: corr || undefined, side }) : undefined}
        onExcel={(account || subcontoMode) ? () => reports.ledgerXlsx('/reports/account-card', {
          organization_id: st.org, account_code: account || undefined, date_from: st.from, date_to: st.to,
          subconto_id: sc || undefined, corr_account_code: corr || undefined, side }) : undefined}
        disabled={!data}>
        <div className="field" style={{ minWidth: 320 }}>
          <label>Рахунок</label>
          <select value={account} onChange={(e) => st.set({ account: e.target.value || null, ...(e.target.value ? {} : { corr: null }) })}>
            <option value="">{sc ? '— усі рахунки (картка субконто) —' : '— оберіть —'}</option>
            {accList.map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 220 }}>
          <label>Кор. рахунок</label>
          <select value={corr} onChange={(e) => st.set({ corr: e.target.value || null })}>
            <option value="">— всі —</option>
            {accList.filter((a) => !a.is_group_only).map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        {sc && (
          <span className="ledger__chip" title="Відбір по субконто (з ОСВ по рахунку або аналізу субконто)">
            {scName || 'субконто'}
            <button type="button" title="зняти відбір" onClick={() => st.set({ sc: null, scName: null, ...(account ? {} : { account: null }) })}>×</button>
          </span>
        )}
        {side && (
          <span className="ledger__chip" title="Лише одна сторона (з шахматки)">
            {side === 'DT' ? 'лише дебет' : 'лише кредит'}
            <button type="button" title="зняти відбір" onClick={() => st.set({ side: null })}>×</button>
          </span>
        )}
        <label className="ledger__check">
          <input type="checkbox" checked={months} onChange={(e) => st.set({ months: e.target.checked ? '1' : null })} />
          підсумки по місяцях
        </label>
      </LedgerFilters>

      {q.isError && <p className="ledger__warn">Не вдалося завантажити картку: перевірте код рахунку.</p>}
      {data?.truncated && (
        <p className="ledger__warn">Показано перші {data.limit} проводок — звузьте період або задайте відбір.</p>
      )}

      <DataTable<Row>
        rows={rows}
        columns={cols}
        rowKey={(r) => r._key}
        loading={q.isLoading}
        emptyText={(account || subcontoMode) ? 'За період проводок немає.' : 'Оберіть рахунок або субконто.'}
        rowBold={(r) => r._kind !== 'entry'}
        rowBackground={(r) => r._kind !== 'entry' ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={openDoc}
        storageKey="ledger-account-card"
      />

      {data && (
        <div className="ledger__totals">
          {/* SUP-321707CF: залишок на початок і на кінець — у рядку, який видно завжди. */}
          <span>Сальдо на початок: <strong>{balanceText(Number(data.totals.opening_dt) - Number(data.totals.opening_cr))}</strong></span>
          <span>Обороти: Дт <strong>{money(data.totals.turnover_dt, { zero: '0,00' })}</strong> · Кт <strong>{money(data.totals.turnover_cr, { zero: '0,00' })}</strong></span>
          <span>Сальдо на кінець: <strong>{balanceText(Number(data.totals.closing_dt) - Number(data.totals.closing_cr))}</strong></span>
          <span>Проводок: <strong>{data.rows.length}</strong></span>
          <span className="ledger__hint">клік по документу — відкрити його</span>
        </div>
      )}
    </div>
  );
}
